<template>
    <div v-click-outside="closeAllMenus">
        <div v-if="loading" role="status" aria-live="polite" class="m-0 flex flex-wrap items-center justify-center">
            <LoadingSpinner width="30px" height="30px" not-centered />
            {{ translations.integrations.common.loading[lang] }}...
        </div>
        <div v-show="!loading">
            <!-- Row 1: Play/Pause + Progress indicator + Speed -->
            <div class="flex items-center gap-2">
                <TtsPlayButton :is-playing="isPlaying" @toggle="playOrPauseAudio" />

                <!-- Progress indicator (visual only, Web Speech API doesn't support seek) -->
                <TtsProgressBar :is-playing="isPlaying" :is-seekable="false" />

                <TtsSpeedControl
                    :speed="playbackSpeed"
                    :is-menu-visible="isSpeedMenuVisible"
                    :is-mobile="onlyMobileVisible"
                    @change="setPlaybackSpeed"
                    @toggle-menu="toggleSpeedMenu"
                    @close-menu="isSpeedMenuVisible = false"
                />
            </div>

            <!-- Row 2: Voice selector -->
            <div v-if="voicesIsReady" class="relative mt-2 border-t border-gray-200 pt-2 dark:border-gray-700">
                <button
                    class="flex w-full cursor-pointer items-center justify-between border-none bg-transparent p-0 text-[13px] text-theme-text"
                    @click="toggleSelectVoice"
                >
                    <span class="truncate opacity-70">
                        {{ localizations[lang].listening.voice }}:
                        <span class="font-medium opacity-100">
                            {{ supportedVoicesForCurrentLang[selectedVoiceIndex].name }}
                        </span>
                        <span class="ml-1 text-[11px]">
                            {{
                                localizations[lang].nativeTtsLang[
                                    supportedVoicesForCurrentLang[selectedVoiceIndex].lang
                                ] || `(${supportedVoicesForCurrentLang[selectedVoiceIndex].lang})`
                            }}
                        </span>
                    </span>
                    <svg-icon icon="chevron-down" width="16px" height="16px" class="ml-1 shrink-0 opacity-60" />
                </button>
                <ChangeVoiceMenu
                    :is-visible="wantSelectVoice"
                    :voices="supportedVoicesForCurrentLang"
                    :selected-voice="selectedVoiceIndex"
                    :open-up="onlyMobileVisible"
                    :title="localizations[lang].listening.voice"
                    @select="changeVoice"
                    @close="wantSelectVoice = false"
                >
                    <template #item="{ item }">
                        {{ item.name }} -
                        {{ localizations[lang].nativeTtsLang[item.lang] || `(${item.lang})` }}
                    </template>
                </ChangeVoiceMenu>
            </div>
        </div>
    </div>
</template>

<script>
'use strict';

import { mapGetters, mapMutations, mapState } from 'vuex';
import ChangeVoiceMenu from './ChangeVoiceMenu.vue';
import LoadingSpinner from '_shared_/UI/Utilities/LoadingSpinner.vue';
import { TTS_DEFAULT_PLAYBACK_SPEED } from '_shared_/js/constants/readerSelectionToolLimits.js';
import TtsPlayButton from './tts/TtsPlayButton.vue';
import TtsProgressBar from './tts/TtsProgressBar.vue';
import TtsSpeedControl from './tts/TtsSpeedControl.vue';

export default {
    name: 'ListenNative',
    components: {
        ChangeVoiceMenu,
        LoadingSpinner,
        TtsPlayButton,
        TtsProgressBar,
        TtsSpeedControl,
    },
    props: {
        text: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            speechApi: window.speechSynthesis,
            textForSpeech: new SpeechSynthesisUtterance(),
            loading: true,
            isSpeaking: false,
            selectedVoiceIndex: 0,
            isPlaying: true,
            wantSelectVoice: false,
            autoClosed: null,
            speechEnded: true,
            voices: [],
            playbackSpeed: TTS_DEFAULT_PLAYBACK_SPEED,
            isSpeedMenuVisible: false,
        };
    },
    computed: {
        ...mapState(['baseEndpoint', 'integrations', 'translations', 'lang', 'localizations', 'ui', 'issue']),
        ...mapGetters(['onlyMobileVisible']),
        supportedVoicesForCurrentLang() {
            if (this.voices.length === 0) {
                return [];
            }

            const lang = this.allowedLangVoices.includes(this.issue.lang) ? this.issue.lang : this.lang;
            return this.voices.filter(voice => {
                return voice.lang.includes(lang);
            });
        },
        voicesIsReady() {
            return this.supportedVoicesForCurrentLang.length > 0;
        },
        isPlayingType() {
            return this.ui.listening.texToListen === this.ui.listening.pageToListen ? 'page' : 'highlight';
        },
        allowedLangVoices() {
            if (this.voices.length === 0) {
                return [];
            }

            return [
                ...this.voices.reduce((set, { lang }) => {
                    const isoLang = lang.split('-')[0];
                    set.add(isoLang);
                    return set;
                }, new Set()),
            ];
        },
    },
    watch: {
        'ui.listening.texToListen'() {
            this.handle();
        },
    },
    mounted() {
        console.log('debug:components tts - native');

        window.addEventListener(
            'pagehide',
            () => {
                if (this.speechApi.speaking) {
                    this.speechApi.cancel();
                }
            },
            false
        );
        this.$nextTick(() => {
            this.setVoicesAndSpeak();
            setTimeout(() => {
                if (!this.voicesIsReady) {
                    this.setVoicesAndSpeak();
                }
            }, 250);
        });
    },
    beforeDestroy() {
        if (this.speechApi.speaking) {
            this.speechApi.cancel();
        }
    },
    methods: {
        ...mapMutations(['closeListeningPlayer']),
        handle() {
            this.loading = true;
            this.clearClosePlayer();

            try {
                const voiceIndexFromLocalStorage = localStorage.getItem(`selectedVoiceIndex:${this.issue.lang}`);

                this.selectedVoiceIndex = voiceIndexFromLocalStorage != null ? voiceIndexFromLocalStorage : 0;
                const validVoiceKey = Object.prototype.hasOwnProperty.call(
                    this.supportedVoicesForCurrentLang,
                    this.selectedVoiceIndex
                )
                    ? this.selectedVoiceIndex
                    : 0;

                if (!this.voicesIsReady) {
                    this.textForSpeech.lang = 'en-US';
                } else {
                    this.textForSpeech.voice = this.supportedVoicesForCurrentLang[validVoiceKey];
                }

                this.textForSpeech.text = this.text;
                this.textForSpeech.rate = this.playbackSpeed;

                /**
                 * On firefox when start `speechSynthesis` this is marked like paused and it's not play the audio.
                 */
                if (this.speechApi.paused) {
                    this.speechApi.resume();
                }

                this.speechApi.cancel();
                setTimeout(() => {
                    /**
                     * We need use a setTimeout because when we canceled previous speack
                     * run speak return error with the API and stop speak.
                     * https://stackoverflow.com/a/44042494
                     */
                    this.speechApi.speak(this.textForSpeech);
                }, 250);

                this.isSpeaking = true;
                this.isPlaying = true;
                this.loading = false;
                this.speechEnded = false;

                this.textForSpeech.onend = () => {
                    /*
                     * We need use a settimeout for avoid problem with know correctly
                     * if SpeecApi is speaking, because is necessary ended first and then start
                     * a new speak. So, we wait a time for run method that check if is spiking and
                     * set payer close.
                     */
                    setTimeout(() => {
                        this.checkIfEnded();
                    }, 250);
                };
            } catch (error) {
                console.error('handle', error);
            }
        },
        setVoicesAndSpeak() {
            try {
                this.voices = this.speechApi.getVoices();
                if (this.voices.length > 0) {
                    this.handle();
                }
            } catch (error) {
                console.error('setVoicesAndSpeak', error);
            }
        },
        checkIfEnded() {
            if (this.speechApi.speaking) return;

            if (!this.speechApi.speaking && this.ui.listening.opened) {
                this.closePlayerWhenIsInnactive();
            }

            this.speechEnded = true;
            this.isPlaying = false;
        },
        playOrPauseAudio() {
            if (this.isPlaying) {
                this.isPlaying = false;
                this.speechApi.pause();
            } else if (this.speechApi.paused) {
                this.isPlaying = true;
                this.speechApi.resume();
            } else {
                this.handle();
            }
        },
        stop() {
            this.isPlaying = false;
            this.speechApi.cancel();
            this.closeListeningPlayer();
        },
        changeVoice(index, firstDispath = false) {
            this.selectedVoiceIndex = index;
            localStorage.setItem(`selectedVoiceIndex:${this.issue.lang}`, this.selectedVoiceIndex);
            this.handle();
            if (firstDispath) return;
            this.toggleSelectVoice();
        },
        toggleSelectVoice() {
            this.wantSelectVoice = !this.wantSelectVoice;
            this.isSpeedMenuVisible = false;
        },
        /**
         * Closes all dropdown menus (voice and speed).
         */
        closeAllMenus() {
            this.wantSelectVoice = false;
            this.isSpeedMenuVisible = false;
        },
        /**
         * Toggles the speed selection menu visibility.
         */
        toggleSpeedMenu() {
            this.isSpeedMenuVisible = !this.isSpeedMenuVisible;
            this.wantSelectVoice = false;
        },
        /**
         * Sets the playback speed and restarts speech with new rate.
         * @param {number} speed - Speed value between 0.5 and 2
         */
        setPlaybackSpeed(speed) {
            this.playbackSpeed = speed;
            this.isSpeedMenuVisible = false;
            this.textForSpeech.rate = speed;
            if (this.speechApi.speaking) {
                this.handle();
            }
        },
        closePlayerWhenIsInnactive() {
            this.isPlaying = false;
            this.isSpeaking = false;
            this.autoClosed = setTimeout(() => {
                if (!this.speechApi.speaking && this.ui.listening.opened) {
                    this.stop();
                }
            }, 5000);
        },
        clearClosePlayer() {
            if (this.autoClosed) {
                clearTimeout(this.autoClosed);
            }
        },
    },
};
</script>
